import { ReactNode, useCallback, useEffect, useReducer, useState } from "react";

import * as LocalStorage from "../local-storage";
import { logger } from "../tracking";

import {
  AuthContext,
  type AuthContextValue,
  type AuthService,
  type PolicyConfig,
} from "./context";
import type { AuthAction, CurrentAuthState, OrgInfo } from "./state";
import {
  UNAUTHENTICATED,
  HYDRATING,
  CREATING_SESSION,
  AUTHENTICATED,
} from "./state";
import { authReducer } from "./reducer";
import { runEffect } from "./effects";

type AuthContextProviderProps = {
  children: ReactNode;
  authService: AuthService;
};

function hydrateState(): CurrentAuthState {
  const token = LocalStorage.getStorageItem("token");
  const orgId = LocalStorage.getStorageItem("currentOrgId");
  const locationId = LocalStorage.getStorageItem("currentLocationId");

  // A stored warehouse is optional. A session that has not entered a
  // warehouse-scoped product has none to restore.
  if (token && orgId) {
    return { status: HYDRATING, token, orgId, locationId: locationId ?? null };
  }

  return { status: UNAUTHENTICATED };
}

function syncToLocalStorage(state: CurrentAuthState) {
  switch (state.status) {
    case CREATING_SESSION:
      LocalStorage.storeIdentifiers({
        userToken: state.token,
        email: state.email,
        orgId: state.orgId,
      });
      break;

    case AUTHENTICATED:
      LocalStorage.storeIdentifiers({
        userToken: state.token,
        email: state.email,
        orgId: state.orgId,
        locationId: state.locationId ?? undefined,
        session: state.sessionData.dakotaSession,
        aaaToken: state.sessionData.aaaToken,
      });
      break;

    case UNAUTHENTICATED:
      LocalStorage.clearLocalStorage();
      LocalStorage.clearCookie();
      break;
  }
}

function loggingReducer(prev: CurrentAuthState, action: AuthAction) {
  const next = authReducer(prev, action);
  logger.info(
    `[AuthState] ${String(prev.status)} → ${String(next.status)} (action: ${action.type})`,
  );
  return next;
}

export default function AuthContextProvider({
  children,
  authService,
}: AuthContextProviderProps) {
  const [state, dispatch] = useReducer(loggingReducer, undefined, hydrateState);
  const [policyConfig, setPolicyConfig] = useState<PolicyConfig | null>(null);

  // Sync to localStorage on state transitions
  useEffect(() => {
    syncToLocalStorage(state);
  }, [state]);

  // Run async side effects - the effect counterpart to the reducer.
  // Returns an AbortController; cleanup aborts in-flight requests
  // if state changes before they complete.
  useEffect(() => {
    const controller = runEffect(state, authService, dispatch);
    return () => {
      controller.abort();
    };
  }, [state, authService]);

  // Fetch policy when authenticated (secondary concern, not a state transition)
  useEffect(() => {
    if (state.status !== AUTHENTICATED) return;

    const controller = new AbortController();
    authService
      .getPolicy()
      .then((policy) => {
        if (!controller.signal.aborted) {
          setPolicyConfig(policy);
        }
      })
      .catch((err) => {
        logger.warn("Failed to fetch policy config", err);
      });

    return () => {
      controller.abort();
    };
  }, [state, authService]);

  // Store org slug when org is selected
  const currentOrgId = "orgId" in state ? state.orgId : null;
  useEffect(() => {
    if (!currentOrgId) return;
    const orgs = "orgs" in state ? state.orgs : [];
    const org = orgs.find((o: OrgInfo) => o.org_id === currentOrgId);
    if (org?.org_slug) {
      LocalStorage.storeIdentifiers({
        orgId: currentOrgId,
        resolvedOrgSlug: org.org_slug,
      });
    }
  }, [currentOrgId, state]);

  // ── Intent actions ──

  const startRedirect = useCallback((token: string, tokenType: string) => {
    dispatch({ type: "START_REDIRECT", token, tokenType });
  }, []);

  const startLogin = useCallback((email: string) => {
    dispatch({ type: "START_LOGIN", email });
  }, []);

  const selectOrg = useCallback((orgId: string) => {
    dispatch({ type: "SELECT_ORG", orgId });
  }, []);

  const submitPassword = useCallback((password: string) => {
    dispatch({ type: "SUBMIT_PASSWORD", password });
  }, []);

  const selectLocation = useCallback((locationId: string) => {
    dispatch({ type: "SELECT_LOCATION", locationId });
  }, []);

  const logout = useCallback(() => {
    dispatch({ type: "LOGOUT" });
    setPolicyConfig(null);
  }, []);

  const value: AuthContextValue = {
    state,
    policyConfig,
    startRedirect,
    startLogin,
    selectOrg,
    submitPassword,
    selectLocation,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
